import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { DashboardTransaction } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { GetCompanyProfileCreationDocument } from './getCompanyProfileCreation.gql';

// We have decided that the empty state is shown only if:
// - the account has been created in the interval [31 days ago -- now]
// - all data received is empty
const HOUR_MS = 3_600_000;
const DAY_MS = HOUR_MS * 24;

type DataDashboardTransaction = Omit<
  DashboardTransaction,
  '__typename' | 'date' | 'oldDate' | 'newDate'
>;

const useDashboardNeedsEmptyState = (args: {
  payin?: DataDashboardTransaction[] | null;
  payout?: DataDashboardTransaction[] | null;
  balance?: DataDashboardTransaction[] | null;
}) => {
  const { companyProfileId } = useCompanyContext();
  const { data, loading } = useQuery(GetCompanyProfileCreationDocument, {
    skip: !companyProfileId,
    variables: {
      companyProfileId: companyProfileId || '',
    },
  });

  return useMemo(() => {
    if (loading || !companyProfileId) {
      return null;
    }

    const profile = data?.viewer.company.profile;
    if (!profile) {
      return false;
    }

    const { balance, payin, payout } = args;
    const mergedValues = ([] as DataDashboardTransaction[])
      .concat(payin || [])
      .concat(payout || [])
      .concat(balance || []);

    // createdAt is already a timestamp in millis
    const { createdAt } = profile;
    const has31DaysOrLess = Date.now() - createdAt <= DAY_MS * 31;
    const isAllZeroes = mergedValues.every(({ newValue, oldValue }) => {
      return (
        (newValue === 0 || newValue === null) &&
        (oldValue === 0 || oldValue === null)
      );
    });

    return has31DaysOrLess && isAllZeroes;
  }, [data, args, loading, companyProfileId]);
};

export default useDashboardNeedsEmptyState;
